// modules/BattleEnemy.js
export default class BattleEnemy {
    constructor(scene) {
        this.scene = scene;
        this.enemies = [];
        this.enemySprites = [];
    }

    init(enemies) {
        this.enemies = enemies.map(enemy => ({
            ...enemy,
            originalHp: enemy.hp,
            originalMaxHp: enemy.maxHp
        }));
    }

    create() {
        this.enemySprites = [];
        
        const screenWidth = this.scene.cameras.main.width;
        const screenHeight = this.scene.cameras.main.height;
        const enemyX = screenWidth * 0.25;
        
        // 上下边距
        const topMargin = screenHeight * 0.1;
        const bottomMargin = screenHeight * 0.1;
        const availableHeight = screenHeight - topMargin - bottomMargin;
        
        this.enemies.forEach((enemy, index) => {
            if (index >= 3) return; // 最多3个敌人
            
            // 均匀分布
            const yPos = topMargin + (availableHeight / 4) * (index + 1);
            const textureKey = enemy.name;
            
            // 创建敌人精灵
            const enemySprite = this.scene.add.image(enemyX, yPos, textureKey);
            enemySprite.setScale(1.5);
            enemySprite.enemyData = enemy;
            enemySprite.index = index;
            
            // 设置为可点击（用于移动端选择目标）
            enemySprite.setInteractive({ useHandCursor: true });
            enemySprite.on('pointerdown', () => {
                // 如果正在选择目标，点击敌人直接选择并确认
                if (this.scene.battleUI && this.scene.battleUI.targetSelection) {
                    const targetSelection = this.scene.battleUI.targetSelection;
                    if (targetSelection.selectingTarget || targetSelection.selectingRangeTarget) {
                        // 找到该敌人在 aliveEnemies 中的索引
                        const aliveEnemies = this.getAliveEnemies();
                        const targetIndex = aliveEnemies.findIndex(e => e === enemySprite);
                        if (targetIndex !== -1) {
                            targetSelection.selectedTargetIndex = targetIndex;
                            // 确认选择
                            const result = targetSelection.confirmTargetSelection();
                            if (result && this.scene.battleUI.events) {
                                this.scene.battleUI.events.emit('targetSelected', result.targetIndex, result.isRangeAttack);
                                targetSelection.clearTargetSelection();
                            }
                        }
                    }
                }
            });
            
            this.enemySprites.push(enemySprite);
            
            // 创建UI元素
            this.createEnemyUI(enemySprite, enemy);
        });
    }

    createEnemyUI(enemySprite, enemy) {
        const x = enemySprite.x;
        const y = enemySprite.y;
        
        // HP条
        const barWidth = 80;
        const barHeight = 8;
        
        // HP条背景
        enemySprite.hpBarBg = this.scene.add.graphics();
        enemySprite.hpBarBg.fillStyle(0x000000, 0.7);
        enemySprite.hpBarBg.fillRect(x - barWidth / 2, y - 70, barWidth, barHeight);
        
        // HP条边框
        enemySprite.hpBarBorder = this.scene.add.graphics();
        enemySprite.hpBarBorder.lineStyle(1, 0xffffff, 0.5);
        enemySprite.hpBarBorder.strokeRect(x - barWidth / 2 - 1, y - 70 - 1, barWidth + 2, barHeight + 2);
        
        // HP条
        enemySprite.hpBar = this.scene.add.graphics();
        this.updateEnemyHPBar(enemySprite);
        
        // 敌人名称
        enemySprite.nameText = this.scene.add.text(
            x - 80,
            y - 90,
            enemy.name,
            {
                fontSize: '14px',
                fill: '#ffffff',
                fontFamily: 'monospace',
                backgroundColor: '#8B0000',
                padding: { left: 5, right: 5, top: 2, bottom: 2 }
            }
        ).setOrigin(0.5, 0.5);
        
        // 状态效果容器
        enemySprite.statusEffectsContainer = this.scene.add.container(x, y - 85);
        enemySprite.statusEffectIcons = [];
    }
    
    updateEnemyStatusEffects(enemySprite) {
        if (!enemySprite.statusEffectsContainer || !enemySprite.enemyData) return;
        
        // 清除旧的状态图标
        if (enemySprite.statusEffectIcons) {
            enemySprite.statusEffectIcons.forEach(icon => {
                if (icon && icon.destroy) icon.destroy();
            });
        }
        enemySprite.statusEffectIcons = [];
        
        const enemy = enemySprite.enemyData;
        const statuses = [];
        let offsetX = -40;
        
        // 燃烧状态
        if (enemy.burning && enemy.burningTurns > 0) {
            statuses.push({
                type: 'burning',
                turns: enemy.burningTurns,
                color: 0xff6600,
                symbol: '🔥'
            });
        }
        
        // 中毒状态
        if (enemy.poisoned && enemy.poisonTurns > 0) {
            statuses.push({
                type: 'poisoned',
                turns: enemy.poisonTurns,
                color: 0x00ff00,
                symbol: '☠'
            });
        }
        
        // 冰冻状态
        if (enemy.frozen && enemy.frozenTurns > 0) {
            statuses.push({
                type: 'frozen',
                turns: enemy.frozenTurns,
                color: 0x00ffff,
                symbol: '❄'
            });
        }
        
        // 创建状态图标
        statuses.forEach((status, index) => {
            // 状态图标背景
            const iconBg = this.scene.add.graphics();
            iconBg.fillStyle(status.color, 0.8);
            iconBg.fillCircle(offsetX + index * 30, 0, 12);
            iconBg.lineStyle(2, 0xffffff, 1);
            iconBg.strokeCircle(offsetX + index * 30, 0, 12);
            
            // 状态图标文字
            const iconText = this.scene.add.text(
                offsetX + index * 30,
                0,
                status.symbol,
                {
                    fontSize: '16px',
                    fill: '#ffffff',
                    fontFamily: 'monospace'
                }
            ).setOrigin(0.5);
            
            // 剩余回合数
            const turnsText = this.scene.add.text(
                offsetX + index * 30,
                15,
                status.turns.toString(),
                {
                    fontSize: '10px',
                    fill: '#ffffff',
                    fontFamily: 'monospace',
                    backgroundColor: '#000000',
                    padding: { left: 3, right: 3, top: 1, bottom: 1 }
                }
            ).setOrigin(0.5);
            
            enemySprite.statusEffectsContainer.add([iconBg, iconText, turnsText]);
            enemySprite.statusEffectIcons.push({ bg: iconBg, text: iconText, turns: turnsText });
        });
    }

    updateEnemyHPBar(enemySprite) {
        if (!enemySprite.hpBar || !enemySprite.enemyData) return;
        
        enemySprite.hpBar.clear();
        
        const x = enemySprite.x;
        const y = enemySprite.y;
        const barWidth = 80;
        const barHeight = 8;
        
        const hpPercent = Math.max(0, enemySprite.enemyData.hp / enemySprite.enemyData.maxHp);
        
        // 根据HP百分比选择颜色
        let color;
        if (hpPercent > 0.6) {
            color = 0xff4444; // 红色
        } else if (hpPercent > 0.3) {
            color = 0xffaa00; // 橙色
        } else {
            color = 0xffff00; // 黄色
        }
        
        enemySprite.hpBar.fillStyle(color, 1);
        enemySprite.hpBar.fillRect(x - barWidth / 2, y - 70, barWidth * hpPercent, barHeight);
        
        // 更新状态效果显示
        this.updateEnemyStatusEffects(enemySprite);
    }

    getAliveEnemies() {
        return this.enemySprites.filter(sprite => 
            sprite.visible && sprite.enemyData.hp > 0
        );
    }

    getAllEnemies() {
        return this.enemySprites;
    }

    getEnemyByIndex(index) {
        if (index >= 0 && index < this.enemySprites.length) {
            return this.enemySprites[index];
        }
        return null;
    }

    getTargetsForRangeAttack(centerIndex, range) {
        const targets = [];
        
        // 获取范围内的敌人
        for (let i = 0; i < this.enemySprites.length; i++) {
            if (Math.abs(i - centerIndex) <= range) {
                const enemy = this.enemySprites[i];
                if (enemy && enemy.visible && enemy.enemyData.hp > 0) {
                    targets.push(enemy);
                }
            }
        }
        
        return targets;
    }

    getDefeatedEnemies() {
        return this.enemies.filter(enemy => enemy.hp <= 0);
    }

    isAllDefeated() {
        return this.enemySprites.every(sprite => 
            !sprite.visible || sprite.enemyData.hp <= 0
        );
    }

    reset() {
        // 重置敌人状态
        this.enemies.forEach((enemy, index) => {
            enemy.hp = enemy.originalHp;
            if (this.enemySprites[index]) {
                this.enemySprites[index].setVisible(true);
                this.updateEnemyHPBar(this.enemySprites[index]);
            }
        });
    }

    destroy() {
        this.enemySprites.forEach(sprite => {
            if (sprite.hpBarBg) sprite.hpBarBg.destroy();
            if (sprite.hpBarBorder) sprite.hpBarBorder.destroy();
            if (sprite.hpBar) sprite.hpBar.destroy();
            if (sprite.nameText) sprite.nameText.destroy();
            if (sprite.statusEffectsContainer) sprite.statusEffectsContainer.destroy();
            if (sprite.statusEffectIcons) {
                sprite.statusEffectIcons.forEach(icon => {
                    if (icon && icon.destroy) icon.destroy();
                });
            }
            if (sprite.destroy) sprite.destroy();
        });
        
        this.enemySprites = [];
        this.enemies = [];
    }
}